/**
 * Public feature-flag kill-switch types and helpers.
 *
 * Matches GET /v1/feature-flags (also /users/feature-flags):
 * effective booleans (maintenance already OR-ed into product flags) plus
 * user-facing messages for soft-block UX.
 */
/** Stable machine-readable error code for 403 kill-switch responses. */
export const FEATURE_DISABLED_ERROR = "FEATURE_DISABLED";
/** Default Spanish messages when the API omits a message for a flag. */
export const DEFAULT_FEATURE_FLAG_MESSAGES = {
    ticket_purchase: "Compras temporalmente deshabilitadas. Probá de nuevo más tarde.",
    swap: "Swap temporalmente deshabilitado. Probá de nuevo más tarde.",
    ticket_transfer: "Transferencias temporalmente deshabilitadas.",
    mercadopago_connect: "Conexión con Mercado Pago temporalmente deshabilitada.",
    user_creation: "Altas de usuarios temporalmente deshabilitadas.",
    maintenance: "Estamos en mantenimiento parcial. Algunas acciones no están disponibles.",
};
/** Fail-open defaults: all product flags enabled, maintenance off. */
export const DEFAULT_FEATURE_FLAGS = {
    ticket_purchase: true,
    swap: true,
    ticket_transfer: true,
    mercadopago_connect: true,
    user_creation: true,
    maintenance: false,
    messages: { ...DEFAULT_FEATURE_FLAG_MESSAGES },
};
const PRODUCT_FLAGS = [
    "ticket_purchase",
    "swap",
    "ticket_transfer",
    "mercadopago_connect",
    "user_creation",
];
/**
 * coerceBool treats missing/invalid values as true (fail-open).
 */
function coerceBool(value, fallback) {
    if (typeof value === "boolean") {
        return value;
    }
    return fallback;
}
/**
 * normalizeFeatureFlags coerces an unknown JSON body into a fail-open FeatureFlagsResponse.
 */
export function normalizeFeatureFlags(raw) {
    const src = raw && typeof raw === "object" && !Array.isArray(raw)
        ? raw
        : {};
    const messagesRaw = src.messages && typeof src.messages === "object" && !Array.isArray(src.messages)
        ? src.messages
        : {};
    const messages = {
        ...DEFAULT_FEATURE_FLAG_MESSAGES,
    };
    for (const [key, value] of Object.entries(messagesRaw)) {
        if (typeof value === "string" && value.trim()) {
            messages[key] = value.trim();
        }
    }
    return {
        ticket_purchase: coerceBool(src.ticket_purchase, true),
        swap: coerceBool(src.swap, true),
        ticket_transfer: coerceBool(src.ticket_transfer, true),
        mercadopago_connect: coerceBool(src.mercadopago_connect, true),
        user_creation: coerceBool(src.user_creation, true),
        maintenance: coerceBool(src.maintenance, false),
        messages,
    };
}
/**
 * isFeatureEnabled reports whether a product entrance is currently allowed.
 * Relies on API effective booleans (maintenance already applied).
 */
export function isFeatureEnabled(flags, flag) {
    return flags[flag] !== false;
}
/**
 * getFeatureFlagMessage returns the soft-block copy for a flag.
 * When maintenance is on, prefers the maintenance message.
 */
export function getFeatureFlagMessage(flags, flag) {
    if (flags.maintenance) {
        const maintenanceMsg = flags.messages.maintenance?.trim();
        if (maintenanceMsg) {
            return maintenanceMsg;
        }
    }
    const msg = flags.messages[flag]?.trim();
    if (msg) {
        return msg;
    }
    return DEFAULT_FEATURE_FLAG_MESSAGES[flag];
}
/**
 * softBlockState returns whether a CTA should be disabled and which message to show.
 * When the flag is enabled, returns { blocked: false, message: null }.
 */
export function softBlockState(flags, flag) {
    if (isFeatureEnabled(flags, flag)) {
        return { blocked: false, message: null };
    }
    return { blocked: true, message: getFeatureFlagMessage(flags, flag) };
}
/**
 * parseFeatureDisabledError extracts a FEATURE_DISABLED payload from a JSON body.
 * Returns null when the body is not a kill-switch response.
 */
export function parseFeatureDisabledError(body) {
    if (!body || typeof body !== "object" || Array.isArray(body)) {
        return null;
    }
    const src = body;
    if (src.error !== FEATURE_DISABLED_ERROR) {
        return null;
    }
    const message = typeof src.message === "string" ? src.message.trim() : "";
    const feature = typeof src.feature === "string" ? src.feature.trim() : "";
    if (!message) {
        return null;
    }
    return {
        error: FEATURE_DISABLED_ERROR,
        feature,
        message,
    };
}
/**
 * extractFeatureDisabledMessage reads FEATURE_DISABLED from a Response-like JSON body
 * or a thrown Error whose message is JSON. Returns the user-facing message or null.
 */
export function extractFeatureDisabledMessage(bodyOrError) {
    if (bodyOrError instanceof Error) {
        try {
            const parsed = JSON.parse(bodyOrError.message);
            return parseFeatureDisabledError(parsed)?.message ?? null;
        }
        catch {
            return null;
        }
    }
    return parseFeatureDisabledError(bodyOrError)?.message ?? null;
}
/**
 * fetchFeatureFlags loads and normalizes the public feature-flags payload.
 * Throws on non-OK HTTP responses; callers should fail open on catch.
 */
export async function fetchFeatureFlags(url, fetchImpl = fetch) {
    const response = await fetchImpl(url, {
        method: "GET",
        headers: { Accept: "application/json" },
        cache: "no-store",
    });
    if (!response.ok) {
        throw new Error(`feature-flags fetch failed: ${response.status}`);
    }
    const raw = (await response.json());
    return normalizeFeatureFlags(raw);
}
/**
 * isProductFeatureFlag is a type guard for known product flag names.
 */
export function isProductFeatureFlag(value) {
    return PRODUCT_FLAGS.includes(value);
}
